import {
    Clock,
    TrendingUp,
    Activity,
    AlertCircle,
    List,
    Columns,
    Grid3X3,
    LayoutDashboard,
    BarChart3,
    Zap,
    ShieldCheck,
    Briefcase,
    Map as MapIcon,
    FolderOpen,
    Users,
    FileText,
    LucideIcon,
    Settings
} from "lucide-react";
import { Project } from "../types";

export const STORAGE_KEY = "zps_epic_prioritizer_v3";
export const SESSIONS_KEY = "zps_epic_sessions_v3";
export const PROJECTS_KEY = "zps_projects_v1";

// Interfaces for UI config
export interface StatusOption {
    value: string;
    label: string;
    color: string;
    icon: LucideIcon;
}

export interface PriorityOption {
    value: string;
    label: string;
    color: string;
}

export interface EffortOption {
    label: string;
    value: 1 | 2 | 3 | 4 | 5;
    color: string;
}

export interface ViewOption {
    id: string;
    label: string;
    icon: LucideIcon;
    shortcut: string;
    isProjectView?: boolean;
    adminOnly?: boolean;
}

export interface AxisOption {
    id: 'wsjf' | 'cod' | 'effort' | 'security' | 'business';
    label: string;
    icon: LucideIcon;
}

export const projectStatusOptions: StatusOption[] = [
    { value: "active", label: "Active", color: "bg-siemens-feedback-green", icon: Activity },
    { value: "on-hold", label: "On Hold", color: "bg-siemens-feedback-orange", icon: AlertCircle },
    { value: "completed", label: "Completed", color: "bg-siemens-feedback-blue", icon: ShieldCheck },
];

export const projectPriorityOptions: PriorityOption[] = [
    { value: "high", label: "High", color: "text-siemens-feedback-red" },
    { value: "medium", label: "Medium", color: "text-siemens-feedback-orange" },
    { value: "low", label: "Low", color: "text-siemens-feedback-blue" },
];

export const DEFAULT_PROJECT: Project = {
    id: "project-legacy",
    userId: "", // Should be set to current user ID generally
    name: "Legacy Improvements",
    description: "Migrated epics from previous version",
    color: "#009999",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
};

export const effortOptions: EffortOption[] = [
    { label: "XS", value: 1, color: "bg-blue-100 text-blue-700" },
    { label: "S", value: 2, color: "bg-indigo-100 text-indigo-700" },
    { label: "M", value: 3, color: "bg-purple-100 text-purple-700" },
    { label: "L", value: 4, color: "bg-fuchsia-100 text-fuchsia-700" },
    { label: "XL", value: 5, color: "bg-pink-100 text-pink-700" },
];

export const laneOptions = ["Unassigned", "Now", "Next", "Later", "Done"] as const;

export const laneColors = {
    Now: {
        bg: "bg-siemens-feedback-green/10 dark:bg-siemens-feedback-green/20",
        border: "border-siemens-feedback-green/30 dark:border-siemens-feedback-green/40",
        text: "text-siemens-feedback-green",
        dot: "bg-siemens-feedback-green",
        icon: Clock
    },
    Next: {
        bg: "bg-siemens-feedback-orange/10 dark:bg-siemens-feedback-orange/20",
        border: "border-siemens-feedback-orange/30 dark:border-siemens-feedback-orange/40",
        text: "text-siemens-feedback-orange",
        dot: "bg-siemens-feedback-orange",
        icon: TrendingUp
    },
    Later: {
        bg: "bg-siemens-feedback-blue/10 dark:bg-siemens-feedback-blue/20",
        border: "border-siemens-feedback-blue/30 dark:border-siemens-feedback-blue/40",
        text: "text-siemens-feedback-blue",
        dot: "bg-siemens-feedback-blue",
        icon: Activity
    },
    Unassigned: {
        bg: "bg-siemens-deep-blue-8 dark:bg-siemens-elevation-base-1",
        border: "border-siemens-divider/30 dark:border-siemens-divider",
        text: "text-siemens-text-soft",
        dot: "bg-siemens-text-soft",
        icon: AlertCircle
    },
    Done: {
        bg: "bg-emerald-100/50 dark:bg-emerald-900/20",
        border: "border-emerald-300/50 dark:border-emerald-700/50",
        text: "text-emerald-600 dark:text-emerald-400",
        dot: "bg-emerald-500",
        icon: ShieldCheck
    },
};

export const viewOptions: ViewOption[] = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, shortcut: "5" },
    { id: "projects", label: "Projects", icon: FolderOpen, shortcut: "0", isProjectView: true },
    { id: "list", label: "List", icon: List, shortcut: "1" },
    { id: "roadmap", label: "Roadmap", icon: MapIcon, shortcut: "2" },
    { id: "kanban", label: "Kanban", icon: Columns, shortcut: "3" },
    { id: "matrix", label: "Matrix", icon: Grid3X3, shortcut: "4" },
    { id: "executive", label: "Executive", icon: BarChart3, shortcut: "6" },
    { id: "admin-users", label: "Settings", icon: Settings, shortcut: "7", adminOnly: true },
    { id: "admin-audit", label: "Audit Logs", icon: FileText, shortcut: "8", adminOnly: true },
];

export const axisOptions: AxisOption[] = [
    { id: "wsjf", label: "WSJF Score", icon: Zap },
    { id: "cod", label: "Cost of Delay", icon: Clock },
    { id: "effort", label: "Effort", icon: Activity },
    { id: "security", label: "Security", icon: ShieldCheck },
    { id: "business", label: "Business Impact", icon: Briefcase },
];
